import { useEffect, useState } from 'react';
import { Activity, TrendingUp, Shield, Wallet, AlertTriangle } from 'lucide-react';
import { api } from '../api/client';

function StatCard({ label, value, sub, accent, icon: Icon }) {
  return (
    <div className="bg-[#13131A] border border-[#1E1E2E] rounded p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-500 font-mono uppercase tracking-widest">{label}</span>
        {Icon && <Icon size={14} className={accent || 'text-slate-600'} />}
      </div>
      <span className={`text-2xl font-mono font-bold ${accent || 'text-slate-200'}`}>{value}</span>
      {sub && <span className="text-[11px] text-slate-500 font-mono">{sub}</span>}
    </div>
  );
}

function StrategyPill({ name, symbol, active, interval }) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5 bg-[#0A0A0F] border border-[#1E1E2E] rounded">
      <div className="flex items-center gap-2">
        <div className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-green-400 animate-pulse' : 'bg-slate-600'}`} />
        <span className="text-xs font-mono text-slate-300">{name}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] font-mono text-amber-500/70">{symbol}</span>
        <span className="text-[10px] font-mono text-slate-600">{interval}s</span>
      </div>
    </div>
  );
}

export default function Overview() {
  const [account, setAccount]     = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [status, setStatus]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  useEffect(() => {
    Promise.all([
      api.getAccount(),
      api.getStrategies(),
      api.getStatus(),
    ])
      .then(([acc, strat, stat]) => {
        setAccount(acc.data);
        setStrategies(strat.data);
        setStatus(stat.data);
      })
      .catch(() => setError('No se pudo conectar con el backend'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex items-center gap-3 text-amber-500 font-mono text-sm">
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
        Conectando...
      </div>
    </div>
  );

  return (
    <div className="p-8 space-y-8">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-100 tracking-tight">Overview</h1>
          <p className="text-xs text-slate-500 font-mono mt-1">
            {new Date().toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })} CDMX
          </p>
        </div>
        {error && (
          <div className="flex items-center gap-2 text-red-400 text-xs font-mono border border-red-400/20 bg-red-400/5 px-3 py-1.5 rounded">
            <AlertTriangle size={12} />
            {error}
          </div>
        )}
      </div>

      {/* Account stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Balance"
          value={account ? `$${parseFloat(account.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
          sub={account?.currency || ''}
          accent="text-amber-400"
          icon={Wallet}
        />
        <StatCard
          label="Equity"
          value={account ? `$${Number(account.equity || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
          sub="Free margin disponible"
          icon={TrendingUp}
        />
        <StatCard
          label="Margen libre"
          value={account ? `$${Number(account.freeMargin || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
          sub={account ? `Leverage 1:${account.leverage}` : ''}
          icon={Shield}
        />
		<StatCard
		  label="Estado"
		  value={account?.tradeAllowed ? 'ACTIVO' : 'MERCADO CERRADO'}
		  sub={account?.server || ''}
		  accent={account?.tradeAllowed ? 'text-green-400' : 'text-slate-500'}
		  icon={Activity}
		/>
      </div>

      {/* Strategies */}
      <div>
        <h2 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">
          Estrategias activas
        </h2>
        <div className="space-y-2">
          {strategies.length > 0 ? strategies.map((s) => (
            <StrategyPill
              key={s.id}
              name={s.name}
              symbol={s.symbol}
              active={s.active}
              interval={s.intervalSeconds || 3600}
            />
          )) : (
            <p className="text-xs text-slate-600 font-mono">Sin estrategias cargadas</p>
          )}
        </div>
      </div>

      {/* Agent status raw */}
      {status && (
        <div>
          <h2 className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">
            Estado del agente
          </h2>
          <pre className="text-[11px] font-mono text-slate-400 bg-[#13131A] border border-[#1E1E2E] rounded p-4 overflow-x-auto">
            {JSON.stringify(status, null, 2)}
          </pre>
        </div>
      )}

    </div>
  );
}